import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { observer } from "mobx-react-lite";
import { useResponseSurveyStore } from "../hooks";
import {
  formatDuration,
  RESPONSES_STATUS_CONFIG,
  PRIORITY_CONFIG,
  DECISION_CONFIG,
  DECISION_STATUS_MAP,
} from "../utils/survey-manager.helper";
import { PATHS } from "@/lib/router";
import AttachmentList from "../components/AttachmentList";
import { AttachmentAnswerList } from "../components/AttachmentAnswerRow";

const PRIORITY_KEYS = ["low", "medium", "high"];

const PriorityBadge = ({ priority }) => {
  const config = PRIORITY_CONFIG[priority];
  if (!config) return null;
  return (
    <span
      className={`px-3 py-1 rounded-md text-xs font-medium dark:font-bold border ${config.activeStyle}`}
    >
      {config.label}
    </span>
  );
};
const ResponsesDetailPage = observer(() => {
  const { responseId } = useParams();
  const navigate = useNavigate();
  const store = useResponseSurveyStore();

  const [priority, setPriority] = useState("");
  const [decision, setDecision] = useState("");
  const [notes, setNotes] = useState("");
  const [nav, setNav] = useState(null);
  const [status, setStatus] = useState("");

  // ── Fetch response by ID ──────────────────────────────────────────────
  useEffect(() => {
    if (!responseId) return;

    const load = async () => {
      try {
        const result = await store.fetchResponseById(responseId);
        console.log("result", JSON.stringify(result, null, 2));
        setPriority(result?.priorityLabel || "");
        setDecision(result?.decisionLabel || "");
        setNotes(result?.notes || "");
        setNav(result?.navigation || null);
        setStatus(result?.status || "");
      } catch {
        navigate(-1); // go back if not found
      }
    };

    load();
  }, [responseId]);

  const navigateToResponseDetail = (id) => {
    if (!id) return;
    navigate(PATHS.RESPONSES_DETAIL.replace(":responseId", id));
  };
  const handleDownloadFile = async (file) => {
    await store.downloadAttachment(file);
  };

  // ── Save changes via store ────────────────────────────────────────────
  const handleSave = async (patch) => {
    try {
      await store.updateResponse(responseId, patch);
    } catch {
      // error already handled in store
    }
  };
  const handleDecisionChange = async (d) => {
    const next = decision === d ? "" : d;
    const newStatus = DECISION_STATUS_MAP[next] ?? "new";

    setDecision(next);
    setStatus(newStatus);

    await handleSave({
      decisionLabel: next || null,
      status: newStatus,
    });
  };

  // ── Loading / not found states ────────────────────────────────────────
  if (store.loading)
    return <div className="p-8 text-muted-foreground">Loading...</div>;
  if (!store.selected)
    return <div className="p-8 text-red-500">Response not found.</div>;

  const data = store.selected;

  return (
    <div className="min-h-screen bg-background">
      {/* ── Top Bar ── */}
      <div className="flex items-center justify-between px-6 py-3 border-b">
        <button
          onClick={() => navigate(PATHS.SURVEY_RESPONSES)}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to responses
        </button>

        <div className="flex items-center gap-3">
          {/* Priority badge */}
          {priority && <PriorityBadge priority={priority} />}

          {/* Status badge */}

          {status && (
            <span
              className={`px-3 py-1 rounded-md text-xs font-medium border
                              ${RESPONSES_STATUS_CONFIG[status]?.style ?? FALLBACK_BADGE}`}
            >
              {RESPONSES_STATUS_CONFIG[status]?.label ?? status}
            </span>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-col lg:flex-row gap-6 p-6 max-w-7xl mx-auto">
        {/* ── Left: Respondent + Answers ── */}
        <div className="flex-1 min-w-0 space-y-6">
          {/* Respondent Card */}
          <div className="border rounded-lg p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {" "}
              {/* ✅ */}
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Name
                </p>
                <p className="font-semibold mt-1">{data.respondent?.name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Company
                </p>
                <p className="font-semibold mt-1">{data.respondent?.company}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Email
                </p>
                <p className="font-semibold mt-1 break-all">
                  {data.respondent?.email}
                </p>{" "}
                {/* ✅ break-all for long emails */}
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Job Title
                </p>
                <p className="font-semibold mt-1">
                  {data.respondent?.jobTitle || "—"}
                </p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground pt-2 border-t">
              Submitted{" "}
              {format(new Date(data.submittedAt), "MMMM d, yyyy, h:mm a")}
              {formatDuration(data.completionTimeInSeconds) && (
                <>
                  {" "}
                  · {formatDuration(data.completionTimeInSeconds)} to complete
                </>
              )}
              {data.inviterEmail && <> · via Email invitation</>}
            </p>
          </div>

          {/* Answers */}
          <div className="space-y-6">
            {data.answers?.length > 0 ? (
              data.answers.map((answer, i) => (
                <div key={i} className="space-y-2">
                  {/* Question number + text */}
                  <div className="flex items-start gap-2">
                    <span className="text-sm font-semibold text-muted-foreground shrink-0">
                      Q{i + 1}.
                    </span>
                    <div className="flex-1">
                      <p className="font-medium text-sm">
                        {answer.questionText}
                      </p>
                    </div>
                  </div>

                  {/* Answer value */}
                  <div className="pl-6">
                    <AnswerDisplay
                      answer={answer}
                      onDownload={handleDownloadFile}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center border rounded-lg">
                <p className="text-muted-foreground text-sm font-medium">
                  No answers found
                </p>
                <p className="text-muted-foreground text-xs mt-1">
                  This response does not contain any answers.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ── Right Sidebar ── */}
        <div className="w-full lg:w-72 lg:shrink-0 space-y-6">
          <div className="border rounded-lg p-4 space-y-5">
            {/* Filtered view / navigation info */}
            {nav && (
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Filtered View
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  All responses
                </p>
                <p className="font-semibold text-sm">
                  Response {nav.current} of {nav.total}
                </p>
              </div>
            )}

            {/* Decision */}
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
                Decision
              </p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(DECISION_CONFIG).map(([key, config]) => (
                  <button
                    key={key}
                    onClick={() => handleDecisionChange(key)}
                    className={`px-3 py-1 text-sm rounded-md border transition-colors font-medium
                    ${
                      decision === key
                        ? config.activeStyle
                        : config.inactiveStyle
                    }
                `}
                  >
                    {config.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Priority */}
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
                Priority
              </p>
              <div className="flex gap-2 flex-wrap">
                {PRIORITY_KEYS.map((p) => {
                  const config = PRIORITY_CONFIG[p];
                  const isSelected = priority === p;
                  return (
                    <button
                      key={p}
                      onClick={() => {
                        setPriority(p);
                        handleSave({ priorityLabel: p });
                      }}
                      className={`px-3 py-1 text-sm rounded-md border transition-colors font-medium
                    ${isSelected ? config?.activeStyle : config?.inactiveStyle}
                `}
                    >
                      {config?.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Attachments */}
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
                Attachments
              </p>
              <AttachmentList attachments={data.attachments ?? []} />
            </div>

            {/* Survey info */}
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
                Survey
              </p>

              {/* ✅ Name + Type badge */}
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-medium text-green-600">
                  {data.surveyId?.name}
                </p>
                {data.surveyId?.type && (
                  <span
                    className="px-2 py-0.5 text-[11px] font-semibold rounded-full
                             bg-muted text-muted-foreground capitalize"
                  >
                    {data.surveyId.type}
                  </span>
                )}
              </div>

              {/* ✅ Questions count */}
              <p className="text-xs text-muted-foreground mt-1">
                {data.answersCount ?? data.answers?.length ?? 0} questions
              </p>
            </div>

            {/* Prev / Next */}
            {nav && (
              <div className="flex items-center justify-between border-t pt-4">
                <button
                  disabled={!nav.prevId}
                  onClick={() => navigateToResponseDetail(nav.prevId)}
                  className="flex items-center gap-1 text-sm disabled:opacity-40 hover:text-foreground"
                >
                  <ChevronLeft className="w-4 h-4" /> Previous
                </button>

                <span className="text-xs text-muted-foreground">
                  {nav.current} of {nav.total}
                </span>

                <button
                  disabled={!nav.nextId}
                  onClick={() => navigateToResponseDetail(nav.nextId)}
                  className="flex items-center gap-1 text-sm disabled:opacity-40 hover:text-foreground"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

const AnswerDisplay = ({ answer, onDownload }) => {
  const value = answer.answer;
  const type = answer.questionType;
  const options = answer.options ?? [];

  // ── Empty / skipped ──────────────────────────────────────────────────────
  if (value === null || value === undefined || value === "") {
    return <span className="text-sm text-muted-foreground italic">—</span>;
  }
  if (type === "attachment") {
    const files = Array.isArray(value) ? value : [];
    return <AttachmentAnswerList files={files} onDownload={onDownload} />;
  }

  // ── Multiple choice / array ──────────────────────────────────────────────
  if (type === "multiple_choice" || Array.isArray(value)) {
    const selected = Array.isArray(value) ? value : [value];

    if (options.length > 0) {
      return (
        <div className="space-y-1.5">
          {options.map((opt, i) => {
            const label = opt.label || opt.value;
            const isSelected =
              selected.includes(opt.value) || selected.includes(opt.label);
            return (
              <div key={i}>
                {isSelected ? (
                  <span
                    className="inline-block px-3 py-1 rounded-md text-sm font-medium
                               border bg-green-50 text-green-700 border-green-200
                               dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/30"
                  >
                    {label}
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground dark:text-gray-500">
                    {label}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className="flex flex-wrap gap-2">
        {selected.map((v, i) => (
          <span
            key={i}
            className="px-3 py-1 rounded-md text-sm border
                       bg-green-50 text-green-700 border-green-200
                       dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/30"
          >
            {v}
          </span>
        ))}
      </div>
    );
  }
  return <span className="text-sm text-muted-foreground">{String(value)}</span>;
};

export default ResponsesDetailPage;
